import { Organization, Provider, Role } from '../../graphQL';

type WithIdAndSimpleOrgs<P extends Provider> = Pick<P, 'id'> & {
  organizations: Pick<Organization, 'id'>[];
};

export const MCP_ROLES: Role[] = [
  Role.UniversityAdmin,
  Role.UniversityBeaconManager,
  Role.UniversityBeaconStaff,
  Role.UniversityReportingAdmin,
  Role.UniversityStaff,
  Role.UniversitySupportingClinician,
];

export function isMcpRole(role: string | null | undefined) {
  return Boolean(role && MCP_ROLES.includes(role as Role));
}

export function isMcpUser(provider: Pick<Provider, 'role'>) {
  return isMcpRole(provider.role);
}

export function isMcpOrReferralUser(provider: Pick<Provider, 'role'>) {
  return isMcpUser(provider) || provider.role === Role.UniversityReferrer;
}

export function isMantraProvider(provider: Pick<Provider, 'role'>) {
  return provider.role === Role.Provider;
}

export function isMantraEmployee(provider: Pick<Provider, 'role'>) {
  return isMantraAdmin(provider) || isMantraProvider(provider);
}

export function isMantraAdmin(provider: Pick<Provider, 'role'>) {
  return provider.role === Role.Admin || provider.role === Role.ProductAdmin;
}

export const isMantraProductAdmin = (provider: Pick<Provider, 'role'>) => {
  return provider.role === Role.ProductAdmin;
};

export function isMcpAdmin(provider: Pick<Provider, 'role'>) {
  return provider.role === Role.UniversityAdmin || provider.role === Role.UniversityReportingAdmin;
}

export function isMantraOrMcpAdmin(provider: Pick<Provider, 'role'>) {
  return isMantraAdmin(provider) || isMcpAdmin(provider);
}

export function isSupportingClinician(provider: Pick<Provider, 'role'>) {
  return provider.role === Role.UniversitySupportingClinician;
}

export function isUniversityReferrer(provider: Pick<Provider, 'role'>) {
  return provider.role === Role.UniversityReferrer;
}

export const BEACON_ROLES: Role[] = [Role.UniversityBeaconManager, Role.UniversityBeaconStaff];

export function isUniversityBeaconRole(provider: Pick<Provider, 'role'>) {
  return BEACON_ROLES.includes(provider.role as Role);
}

export function isUniversityStaff(provider: Pick<Provider, 'role'>) {
  return provider.role === Role.UniversityStaff;
}

export function hasOrganizations(provider: WithIdAndSimpleOrgs<Provider>) {
  return Boolean(provider.organizations.length);
}

export function belongsToOrganization(
  provider: WithIdAndSimpleOrgs<Provider>,
  organizationId: number
) {
  return provider.organizations.some(o => o.id === organizationId);
}
